import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../../services/api';
import endpoints from '../../../utils/commonVariables/endpoints';
import { viewModelEntities } from '../../../utils/commonVariables/entityKeys';
import useEncodedUrlId from '../../../customHooks/useEncodedUrlId';
import '../../../stylesheets/stylesLayout/privateDetail.scss';
import { useNotification } from '../../../contexts/NotificationsContext';
import Tooltip from '../../../muiComponents/Tooltip';
import routes from '../../../utils/commonVariables/routes';
import IconButton from '../../../muiComponents/IconButton';
import supplierBookingViewModel from '../../../utils/viewModelStructures/SupplierBooking';
import {
    BookingState,
    CreditCardState,
} from '../../../types/booking/BookingTypes';
import {
    creditCardInitialState,
    detailBookingInitialState,
} from '../../../utils/bookings/bookingUtils';
import CopyOnHover from '../components/CopyOnHover';
import MainInformation from './bookingDetail/MainInformation';
import HotelInfo from './bookingDetail/HotelInfo';
import Finance from './bookingDetail/Finance';
import Notes from './bookingDetail/Notes';
import { adaptDDBBBookingToState } from '../../../services/booking/bookingAdapters';
import DotIcon from '../../components/DotIcon';
import BookingDetailLoading from './bookingDetail/BookingDetailLoading';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import useTranslate from '../../../customHooks/useTranslate';
import useLanguage from '../../../customHooks/useLanguage';
import RoomsList from './RoomsList';

const BookingDetail = () => {
    const translate = useTranslate();
    const language = useLanguage();
    const { setErrorMessage } = useNotification();

    const history = useHistory();
    const [booking, setBooking] = useState<BookingState>(
        detailBookingInitialState
    );
    const [creditCard, setCreditCard] = useState<CreditCardState>(
        creditCardInitialState
    );
    const [isFetching, setIsFetching] = useState(false);

    const IdBooking = useEncodedUrlId('encodedIdBooking');
    const getBooking = async () => {
        setIsFetching(true);
        const endpoint = endpoints.viewModel(
            viewModelEntities.SupplierBooking,
            IdBooking
        );
        const {
            data: { Success, Result: _booking, Messages = [] },
        } = await api.post(endpoint, supplierBookingViewModel);
        if (Success && _booking) {
            const cleanBooking = adaptDDBBBookingToState(_booking);

            setBooking(cleanBooking);
            if (cleanBooking.paymentId > 0) {
                await getPaymentInfo(cleanBooking.paymentId);
                setCreditCard(prev => ({
                    ...prev,
                    creditCardHolderName:
                        _booking?.SupplierBookingPaymentInfo?.HolderName || '',
                    cvc:
                        _booking?.SupplierBookingPaymentInfo?.VirtualCreditCard
                            ?.CVV || '',
                }));
            }
        } else {
            const errorMsg =
                Messages?.[0]?.Title || FeedbackMessages.COMMON_ERROR;
            setErrorMessage(errorMsg);
        }
        setIsFetching(false);
    };

    useEffect(() => {
        if (IdBooking) {
            getBooking();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [IdBooking, language]);

    const getPaymentInfo = async (paymentId: number) => {
        const endpoint = endpoints.getCreditCardNumber(paymentId);
        const { data } = await api.get(endpoint);
        const { Result: _cardDetails, StatusCode } = data;
        if (StatusCode === 200) {
            setCreditCard(prev => ({
                ...prev,
                creditCardNumber: _cardDetails.CreditCardNumber
                    ? _cardDetails?.CreditCardNumber.replace(/-/g, '')
                    : '',
                creditCardExpiryDate: _cardDetails?.ExpirationDate || '',
            }));
        }
    };

    const closePage = () => {
        history.push(routes.landingPrivate + routes.booking);
    };

    const bookingSupplements = useMemo(() => {
        return booking.rooms.flatMap(room => room.supplements);
    }, [booking.rooms]);

    const hasSupplements = bookingSupplements.length > 0;
    const firstRoomWithSupplements = booking.rooms.find(
        room => room.supplements.length > 0
    );
    const supplementsDetailsLink = firstRoomWithSupplements
        ? `#room-${firstRoomWithSupplements.id}-supplements`
        : '';

    return (
        <div className='booking-detail__container'>
            {isFetching ? (
                <BookingDetailLoading />
            ) : (
                <>
                    <div className='booking__title flex-between flex-align-center'>
                        <Tooltip titleText='booking'>
                            <span>
                                <CopyOnHover textToCopy={booking.bookingCode}>
                                    <h1>
                                        {translate('booking')}:{' '}
                                        {booking.bookingCode}
                                    </h1>
                                </CopyOnHover>
                            </span>
                        </Tooltip>
                        <div className='flex gap-20'>
                            <div className='flex flex-align-center gap-5'>
                                <DotIcon
                                    restStyles={{ color: booking.statusColor }}
                                />
                                <p>{booking.status}</p>
                            </div>
                            <IconButton iconName='Close' onClick={closePage} />
                        </div>
                    </div>
                    <main className='booking__main'>
                        <MainInformation
                            bookingCode={booking.bookingCode}
                            hotelRefecerenceCode={booking.hotelRefecerenceCode}
                            bookingDate={booking.bookingDate}
                            checkIn={booking.checkIn}
                            checkOut={booking.checkOut}
                            customerName={booking.customerName}
                            nights={booking.nights}
                            paxes={booking.paxes}
                            totalAmount={booking.totalAmount}
                            currency={booking.currency}
                            isCancelled={booking.isCancelled}
                            hasSupplements={hasSupplements}
                            supplements={bookingSupplements}
                            supplementsDetailsLink={supplementsDetailsLink}
                        />
                        <HotelInfo
                            idHotel={booking.idHotel}
                            hotel={booking.hotel}
                            rating={booking.hotelRating}
                            hotelCategory={booking.hotelCategory}
                            address={booking.address}
                            hotelImages={booking.hotelImages}
                        />
                        <RoomsList
                            rooms={booking.rooms}
                            nights={booking.nights}
                            checkIn={booking.checkIn}
                            currencyKey={booking.currency}
                        />
                        <Finance
                            paymentId={booking.paymentId}
                            creditCard={creditCard}
                            isCancelled={booking.isCancelled}
                            paymentAmount={booking.paymentAmount}
                            paymentCurrency={booking.paymentCurrency}
                            cancellationPolicies={booking.cancellationPolicies}
                        />
                        {booking.notes ? <Notes notes={booking.notes} /> : ''}
                    </main>
                </>
            )}
        </div>
    );
};

export default BookingDetail;
